import {
  Component,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from "@angular/core";
import { CachedProjectService } from "../../../shared/services/cached-project.service";
import { JobStateService } from "../../../projects/project/model-new/job-state.store";
import { NzNotificationService } from "ng-zorro-antd/notification";
import { LogEntry } from "../../../models/jobLog";
import { filter, Subject, take, takeUntil } from "rxjs";
import { CommonModule } from "@angular/common";
import { NzSpinComponent } from "ng-zorro-antd/spin";
import { NzDividerModule } from "ng-zorro-antd/divider";
import { NzTooltipModule } from "ng-zorro-antd/tooltip";
import { NzEmptyModule } from "ng-zorro-antd/empty";
import { NzBadgeModule } from "ng-zorro-antd/badge";
import { NzTagModule } from "ng-zorro-antd/tag";
import { NzIconModule } from "ng-zorro-antd/icon";
import { NzButtonModule } from "ng-zorro-antd/button";
import { NzTabsModule } from "ng-zorro-antd/tabs";
import { NzCollapseModule } from "ng-zorro-antd/collapse";
import { Paginator } from "../../../shared/services/paginator";
import { ProjectService } from "../../../shared/services/project.service";
import { rxResource } from "@angular/core/rxjs-interop";
import { NzSelectModule } from "ng-zorro-antd/select";
import { FormsModule } from "@angular/forms";

export type LogView = "summary" | "list";

@Component({
  selector: "app-output-logs",
  imports: [
    CommonModule,
    NzSpinComponent,
    NzTabsModule,
    NzButtonModule,
    NzIconModule,
    NzTagModule,
    NzBadgeModule,
    NzEmptyModule,
    NzTooltipModule,
    NzDividerModule,
    NzCollapseModule,
    NzSelectModule,
    FormsModule,
  ],
  templateUrl: "./output-logs.component.html",
  styleUrl: "./output-logs.component.less",
})
export class OutputLogsComponent {
  private cachedProjectService = inject(CachedProjectService);
  private projectService = inject(ProjectService);
  private jobStateService = inject(JobStateService);
  private nzNotificationService = inject(NzNotificationService);
  private destroyRef$ = new Subject<void>();

  protected isLoading: boolean = false;
  protected errorsPaginator!: Paginator<LogEntry>;
  protected warningsPaginator!: Paginator<LogEntry>;
  protected isDownloading: boolean;
  protected logFiles: string[];
  protected selectedFile = signal<string | null>(null);

  // View toggle: 'summary' shows grouped summary, 'list' shows paginated full list
  protected errorsView: LogView = "summary";
  protected warningsView: LogView = "summary";

  constructor() {
    effect(() => {
      this.selectedFile.set(null); // reset on job change
      const jobId = this.jobStateService.jobData().id;
      this.getLogFiles(jobId);
    });

    effect(() => {
      const jobId = untracked(() => this.jobStateService.jobData().id);
      const selectedFile = this.selectedFile();
      if (!selectedFile) return;
      this.initPaginators(jobId, selectedFile);
    });
  }

  private getLogFiles(jobId: string) {
    this.isLoading = true;
    this.cachedProjectService
      .getJobOutputLogFiles(jobId)
      .pipe(take(1))
      .subscribe({
        next: (files) => {
          this.logFiles = files;
          this.selectedFile.set(files[0] ?? null);
        },
        error: (err) => {
          console.error("Failed to load log files for job", jobId, err);
          this.nzNotificationService.error(
            "Error",
            "Failed to load log files: " +
              (err.error.detail ?? err.message ?? "Unknown error"),
          );
        },
        complete: () => {
          this.isLoading = false;
        },
      });
  }

  protected fileWarningsSummary = rxResource({
    params: () => {
      const file = this.selectedFile();
      if (!file) return null;
      const jobId = untracked(() => this.jobStateService.jobData().id);
      return { jobId, fileName: file };
    },
    stream: ({ params }) => {
      if (!params) return null;
      return this.cachedProjectService.getJobOutputWarningsSummary(
        params.jobId,
        params.fileName,
      );
    },
  });

  protected fileErrorsSummary = rxResource({
    params: () => {
      const file = this.selectedFile();
      if (!file) return null;
      const jobId = untracked(() => this.jobStateService.jobData().id);
      return { jobId, fileName: file };
    },
    stream: ({ params }) => {
      if (!params) return null;
      return this.cachedProjectService.getJobOutputErrorsSummary(
        params.jobId,
        params.fileName,
      );
    },
  });

  protected errorsTotal = computed(() => {
    if (this.fileErrorsSummary.error()) return 0;
    return (this.fileErrorsSummary?.value() ?? []).reduce(
      (sum, e) => sum + e.count,
      0,
    );
  });

  protected warningsTotal = computed(() => {
    if (this.fileWarningsSummary.error()) return 0;
    return (this.fileWarningsSummary?.value() ?? []).reduce(
      (sum, w) => sum + w.count,
      0,
    );
  });

  private initPaginators(id: string, selectedFile: string) {
    this.isLoading = true;
    // destroy if they already exist to avoid memory leaks — mainly when the job id changes
    this.destroyRef$.next();
    this.errorsPaginator?.destroy();
    this.warningsPaginator?.destroy();

    this.errorsPaginator = this.cachedProjectService.createPaginator(
      this.cachedProjectService.getJobOutputErrors(id, selectedFile),
    );
    this.warningsPaginator = this.cachedProjectService.createPaginator(
      this.cachedProjectService.getJobOutputWarnings(id, selectedFile),
    );

    this.errorsPaginator.error$
      .pipe(
        takeUntil(this.destroyRef$),
        filter((err) => !!err),
      )
      .subscribe((err) => {
        this.nzNotificationService.error(
          "Error",
          "Failed to load error logs: " +
            (err.error.detail ?? err.message ?? "Unknown error"),
        );
      });
    this.warningsPaginator.error$
      .pipe(
        takeUntil(this.destroyRef$),
        filter((err) => !!err),
      )
      .subscribe((err) => {
        this.nzNotificationService.error(
          "Error",
          "Failed to load warning logs: " +
            (err.error.detail ?? err.message ?? "Unknown error"),
        );
      });

    this.errorsPaginator.load();
    this.warningsPaginator.load();
    this.isLoading = false;
  }

  ngOnDestroy() {
    this.errorsPaginator?.destroy();
    this.warningsPaginator?.destroy();
    this.destroyRef$.next();
    this.destroyRef$.complete();
  }

  download() {
    this.isDownloading = true;
    this.projectService
      .downloadJobOutput(this.jobStateService.jobData().id)
      .pipe(take(1))
      .subscribe({
        next: (blob) => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `job-output-${this.jobStateService.jobData().id}.zip`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        },
        error: (err) => {
          this.nzNotificationService.error(
            "Error",
            "Failed to download logs: " + err.message,
          );
        },
        complete: () => (this.isDownloading = false),
      });
  }
}
